import React, { useState } from 'react';
import DataTable from 'react-data-table-component';
import { IoIosArrowDown } from 'react-icons/io';
import { IoIosArrowUp } from 'react-icons/io';

const AnomaliesTable = ({ data, selectedTags, onRowSelect }) => {
    const [expandedRowKey, setExpandedRowKey] = useState(null);

    const buildRowKey = row => `${row.DEP_TAG_ALIAS}_${row.TIMESTAMP}`;

    // Row click only highlights
    const handleRowClick = row => {
        if (onRowSelect) {
            onRowSelect(row);
        }
    };

    // Button click toggles dropdown
    const handleExpandClick = (row, e) => {
        // Prevent row click event when toggling details.
        e.stopPropagation();
        const key = buildRowKey(row);

        if (expandedRowKey === key) {
            setExpandedRowKey(null);
            return;
        }

        setExpandedRowKey(key);
    };

    const columns = [
        {
            name: 'Tag',
            selector: row => row.DEP_TAG_ALIAS,
            sortable: true,
            cell: row => (
                <span title={row.DEP_TAG_ALIAS} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'120px'}}>{row.DEP_TAG_ALIAS}</span>
            )
        },
        {
            name: 'Timestamp',
            selector: row => row.TIMESTAMP,
            sortable: true,
            cell: row => <div title={row.TIMESTAMP} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'140px'}}>{row.TIMESTAMP}</div>
        },
        {
            name: 'Value',
            selector: row => row.SNSR_VALUE,
            sortable: true,
            cell: row => <div title={row.SNSR_VALUE} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'80px'}}>{row.SNSR_VALUE}</div>
        },
        {
            name: 'Anomaly',
            selector: row => row.FLG_ANOMALIES,
            sortable: true,
            cell: row => <div title={row.FLG_ANOMALIES} style={{whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:'80px'}}>{row.FLG_ANOMALIES}</div>
        },
        {
            name: '',
            button: true,
            width: '56px',
            cell: row => {
                const key = buildRowKey(row);
                let ariaLabel = 'Expand details';
                let icon = <IoIosArrowDown />;

                if (expandedRowKey === key) {
                    ariaLabel = 'Collapse details';
                    icon = <IoIosArrowUp />;
                }

                return (
                    <button
                        onClick={e => handleExpandClick(row, e)}
                        aria-label={ariaLabel}
                    >
                        {icon}
                    </button>
                );
            }
        }
    ];

    const customStyles = {
        headCells: {
            style: {
                fontSize: '13px',
                backgroundColor: '#e3e1e1'
            }
        },
        cells: {
            style: {
                fontSize: '13.4px'
            }
        },
        table: {
            style: {
                border: '1px solid #e3e1e1',
                margin: '0 auto'
            }
        }
    };

    const conditionalRowStyles = [
        {
            when: row => buildRowKey(row) === expandedRowKey,
            style: {
                borderBottom: 'none',
                backgroundColor: '#ededed'
            }
        }
    ];

    // Convert object format to array of anomaly rows
    const getAnomalyRows = sourceData => {
        if (!sourceData) {
            return [];
        }
        if (!sourceData.tag_aliases) {
            return [];
        }
        if (!sourceData.timestamps) {
            return [];
        }
        if (!sourceData.values) {
            return [];
        }
        if (!sourceData.anomalies) {
            return [];
        }

        const rows = [];
        sourceData.timestamps.forEach((timestamp, tIdx) => {
            sourceData.tag_aliases.forEach(tag => {
                // Handle both string '1' and number 1 for anomaly flags.
                const anomalyFlag = sourceData.anomalies[tag][tIdx];
                const isAnomaly = anomalyFlag === 1 || anomalyFlag === '1';

                if (!isAnomaly) {
                    return;
                }

                let highLimit;
                if (sourceData.high_limits) {
                    highLimit = sourceData.high_limits[tag][tIdx];
                }

                let lowLimit;
                if (sourceData.low_limits) {
                    lowLimit = sourceData.low_limits[tag][tIdx];
                }

                rows.push({
                    DEP_TAG_ALIAS: tag,
                    TIMESTAMP: timestamp,
                    SNSR_VALUE: sourceData.values[tag][tIdx],
                    FLG_ANOMALIES: sourceData.anomalies[tag][tIdx],
                    HIGH_LIMIT: highLimit,
                    LOW_LIMIT: lowLimit,
                    INFERENCE_JOB_NAME: sourceData.inference_job_name || '',
                    FORECAST_JOB_NAME: sourceData.FORECAST_JOB_NAME || sourceData.forecast_job_name || '',
                    CREATED_AT: sourceData.created_at || ''
                });
            });
        });
        return rows;
    };

    const anomalyRows = getAnomalyRows(data);

    // Filter data based on selected tags.
    let filteredData = anomalyRows;
    if (selectedTags && selectedTags.length > 0) {
        const selectedAliases = selectedTags.map(tag => tag.value || tag.DEP_TAG_ALIAS);
        filteredData = anomalyRows.filter(item => selectedAliases.includes(item.DEP_TAG_ALIAS));
    }

    return (
        <>
            <div className='w-[65%] '>
                <p className='font-semibold text-2xl text-btn-primary w-[65%] mb-5'>{`Anomalies Detected (${filteredData.length})`}</p>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    paginationTotalRows={filteredData.length}
                    pagination
                    paginationPerPage={5}
                    paginationRowsPerPageOptions={[5, 10, 15, 20]}
                    highlightOnHover
                    responsive
                    customStyles={customStyles}
                    expandableRows
                    expandableRowsHideExpander
                    expandableRowExpanded={row => buildRowKey(row) === expandedRowKey}
                    onRowClicked={handleRowClick}
                    expandableRowsComponent={ExpandedComponent}
                    conditionalRowStyles={conditionalRowStyles}
                />
            </div>
        </>
    );
};

export default AnomaliesTable;

const ExpandedComponent = ({ data }) => {
    let jobNameLabel = 'Inference Job Name :';
    let jobNameValue = data.INFERENCE_JOB_NAME;

    if (data.FORECAST_JOB_NAME) {
        jobNameLabel = 'Forecast Job Name :';
        jobNameValue = data.FORECAST_JOB_NAME;
    }

    return (
        <div style={{ padding: '10px 20px', fontSize: '13px', backgroundColor: '#f9f9f9', display: 'flex', gap: '50px' }}>
            <div>
                <p className='mb-1'><strong>Created Date : </strong> {data.CREATED_AT}</p>
                <p className='mb-1 '><strong>DGS Tags:</strong> {data.DEP_TAG_ALIAS}</p>
                <p className='mb-1'><strong>High Limit:</strong> {data.HIGH_LIMIT ?? '-'}</p>
                <p className='mb-1'><strong>Low Limit:</strong> {data.LOW_LIMIT ?? '-'}</p>
                <p className='mb-1'><strong>Sensor Value :</strong> {data.SNSR_VALUE}</p>
                <p className='mb-1'><strong>{jobNameLabel}</strong> {jobNameValue}</p>
                <p className='mb-1'><strong>Timestamp :</strong> {data.TIMESTAMP}</p>
            </div>
        </div>
    );
};
